import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectorRef,
  AfterViewInit,
  inject
} from "@angular/core";

import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";

@Component({
  selector: "app-image-cropper-dialog",
  templateUrl: "./image-cropper-dialog.component.html",
  styleUrls: ["./image-cropper-dialog.component.scss"],
  imports: [MatDialogModule, MatButtonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ImageCropperDialogComponent implements OnInit, AfterViewInit {
  dialogRef = inject<MatDialogRef<ImageCropperDialogComponent>>(MatDialogRef);
  data = inject<{
    imageFile: File;
  }>(MAT_DIALOG_DATA);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild("sourceCanvas", { static: true })
  sourceCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild("previewCanvas", { static: true })
  previewCanvas: ElementRef<HTMLCanvasElement>;

  imageSource: HTMLImageElement;
  croppedImage: string = "";

  // Circle position and size
  circleX: number = 100;
  circleY: number = 100;
  circleRadius: number = 50;

  // For drag operations
  isDragging: boolean = false;
  isResizing: boolean = false;
  lastMouseX: number = 0;
  lastMouseY: number = 0;

  // Canvas dimensions
  canvasWidth: number = 0;
  canvasHeight: number = 0;

  // Added to prevent infinite retries
  private retryCount = 0;
  private maxRetries = 10;
  private viewInitialized = false;

  ngOnInit(): void {
    console.log(
      "[ImageCropper] ngOnInit called, has image file:",
      !!this.data?.imageFile
    );
  }

  ngAfterViewInit(): void {
    console.log(
      "[ImageCropper] ngAfterViewInit called, canvases available:",
      !!this.sourceCanvas,
      !!this.previewCanvas
    );

    this.viewInitialized = true;

    // Wait until view is initialized before loading the image
    if (this.data && this.data.imageFile) {
      console.log(
        "[ImageCropper] Image file size:",
        this.data.imageFile.size,
        "bytes"
      );
      this.loadImageFromFile(this.data.imageFile);
    }
  }

  loadImageFromFile(file: File): void {
    console.log("[ImageCropper] loadImageFromFile called");
    const reader = new FileReader();
    reader.onload = (e: any) => {
      console.log("[ImageCropper] FileReader onload triggered");
      this.imageSource = new Image();
      this.imageSource.onload = () => {
        console.log("[ImageCropper] Image loaded, dimensions:", {
          width: this.imageSource.width,
          height: this.imageSource.height
        });
        // Initialize canvas with image dimensions
        setTimeout(() => {
          console.log("[ImageCropper] Calling initializeCanvas after timeout");
          this.initializeCanvas();
        }, 0);
      };
      this.imageSource.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  initializeCanvas(): void {
    console.log(
      "[ImageCropper] initializeCanvas called, has canvas:",
      !!this.sourceCanvas,
      "has image:",
      !!this.imageSource
    );
    if (!this.sourceCanvas || !this.imageSource) {
      console.warn("[ImageCropper] Canvas or image source not available");
      return;
    }

    const ctx = this.sourceCanvas.nativeElement.getContext("2d");

    // Set canvas dimensions
    this.canvasWidth = Math.min(500, this.imageSource.width);
    this.canvasHeight = Math.min(
      500,
      this.imageSource.height * (this.canvasWidth / this.imageSource.width)
    );

    console.log("[ImageCropper] Setting canvas dimensions:", {
      width: this.canvasWidth,
      height: this.canvasHeight
    });

    this.sourceCanvas.nativeElement.width = this.canvasWidth;
    this.sourceCanvas.nativeElement.height = this.canvasHeight;

    // Initial circle position in center of canvas
    this.circleX = this.canvasWidth / 2;
    this.circleY = this.canvasHeight / 2;
    this.circleRadius = Math.min(this.canvasWidth, this.canvasHeight) / 4;

    console.log("[ImageCropper] Initial circle position:", {
      x: this.circleX,
      y: this.circleY,
      radius: this.circleRadius
    });

    // Draw image and circle
    this.drawCanvas();

    // Update preview
    this.updatePreview();
  }

  drawCanvas(): void {
    if (!this.sourceCanvas || !this.imageSource) return;

    const ctx = this.sourceCanvas.nativeElement.getContext("2d");

    // Clear canvas
    ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

    // Draw image
    ctx.drawImage(this.imageSource, 0, 0, this.canvasWidth, this.canvasHeight);

    // Draw a semi-transparent overlay outside the circle
    ctx.save();
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

    // Create a clipping region for the circle
    ctx.beginPath();
    ctx.arc(this.circleX, this.circleY, this.circleRadius, 0, Math.PI * 2);
    ctx.clip();

    // Clear the circle area to show the image
    ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    ctx.drawImage(this.imageSource, 0, 0, this.canvasWidth, this.canvasHeight);
    ctx.restore();

    // Draw circle border
    ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(this.circleX, this.circleY, this.circleRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Draw resize handle
    ctx.fillStyle = "white";
    ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
    ctx.lineWidth = 1;

    // Draw larger handle for easier targeting
    ctx.beginPath();
    ctx.arc(this.circleX + this.circleRadius, this.circleY, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  updatePreview(): void {
    console.log("[ImageCropper] Starting updatePreview method");

    // Make sure we don't get into an infinite retry loop
    if (this.retryCount >= this.maxRetries) {
      console.error(
        "[ImageCropper] Maximum retry count reached, giving up on preview update"
      );
      return;
    }

    // Add defensive checks
    if (!this.sourceCanvas || !this.imageSource) {
      console.error(
        "[ImageCropper] Source canvas or image not available for preview"
      );
      return;
    }

    // Ensure the preview canvas is available - if not retry later
    if (!this.previewCanvas && this.viewInitialized) {
      console.warn(
        "[ImageCropper] Preview canvas not available, will retry in 100ms (attempt " +
          (this.retryCount + 1) +
          " of " +
          this.maxRetries +
          ")"
      );
      this.retryCount++;
      setTimeout(() => this.updatePreview(), 100);
      return;
    }

    // Exit if preview canvas is still not available
    if (!this.previewCanvas) {
      console.error(
        "[ImageCropper] Preview canvas still not available after view init, something is wrong"
      );
      return;
    }

    try {
      const previewCtx = this.previewCanvas.nativeElement.getContext("2d");

      // Set preview canvas size
      this.previewCanvas.nativeElement.width = 120;
      this.previewCanvas.nativeElement.height = 120;

      // Clear preview canvas
      previewCtx.clearRect(0, 0, 120, 120);

      // Create circular clipping path
      previewCtx.beginPath();
      previewCtx.arc(60, 60, 60, 0, Math.PI * 2);
      previewCtx.clip();

      // Get image data from source canvas
      const sourceX = Math.max(0, this.circleX - this.circleRadius);
      const sourceY = Math.max(0, this.circleY - this.circleRadius);
      const sourceWidth = Math.min(
        this.canvasWidth - sourceX,
        this.circleRadius * 2
      );
      const sourceHeight = Math.min(
        this.canvasHeight - sourceY,
        this.circleRadius * 2
      );

      console.log("[ImageCropper] Source coordinates:", {
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight
      });

      // Draw cropped image on preview canvas
      previewCtx.drawImage(
        this.sourceCanvas.nativeElement,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        120,
        120
      );

      try {
        // Generate the cropped image data
        const dataUrl = this.previewCanvas.nativeElement.toDataURL("image/png");

        // Set the cropped image property
        this.croppedImage = dataUrl;

        // Reset retry counter as we succeeded
        this.retryCount = 0;

        // Log cropped image details
        console.log(
          "[ImageCropper] Generated cropped image, length:",
          this.croppedImage.length
        );
        console.log(
          "[ImageCropper] Image data starts with:",
          this.croppedImage.substring(0, 40) + "..."
        );

        // Force change detection
        this.cdr.detectChanges();

        console.log(
          "[ImageCropper] hasCroppedImage() returns:",
          this.hasCroppedImage()
        );
      } catch (err) {
        console.error("[ImageCropper] Error generating image data URL:", err);
      }
    } catch (err) {
      console.error("[ImageCropper] Error in updatePreview:", err);
    }
  }

  // Helper method to check if we have a valid cropped image
  hasCroppedImage(): boolean {
    console.log(
      "[ImageCropper] hasCroppedImage called, croppedImage exists:",
      !!this.croppedImage
    );

    if (!this.croppedImage) {
      console.log("[ImageCropper] No cropped image available");
      return false;
    }

    // Simplified check - if we have any data that starts with the data URL prefix, consider it valid
    return this.croppedImage.startsWith("data:image/");
  }

  // Mouse event handlers
  onMouseDown(event: MouseEvent): void {
    const rect = this.sourceCanvas.nativeElement.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    // Check if clicked on resize handle
    const distToResizeHandle = Math.sqrt(
      Math.pow(mouseX - (this.circleX + this.circleRadius), 2) +
        Math.pow(mouseY - this.circleY, 2)
    );

    if (distToResizeHandle <= 8) {
      // Resize operation
      this.isResizing = true;
      this.isDragging = false;
      this.lastMouseX = mouseX;
      this.lastMouseY = mouseY;
      event.preventDefault();
      return;
    }

    // Check if clicked inside circle
    const distToCenter = Math.sqrt(
      Math.pow(mouseX - this.circleX, 2) + Math.pow(mouseY - this.circleY, 2)
    );

    if (distToCenter <= this.circleRadius) {
      // Move operation
      this.isDragging = true;
      this.isResizing = false;
      this.lastMouseX = mouseX;
      this.lastMouseY = mouseY;
      event.preventDefault();
    }
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging && !this.isResizing) return;

    const rect = this.sourceCanvas.nativeElement.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const deltaX = mouseX - this.lastMouseX;
    const deltaY = mouseY - this.lastMouseY;

    if (this.isResizing) {
      // Resize operation based on distance from center
      const newDist = Math.sqrt(
        Math.pow(mouseX - this.circleX, 2) + Math.pow(mouseY - this.circleY, 2)
      );

      this.circleRadius = Math.max(20, newDist);

      // Ensure circle stays within canvas
      this.circleRadius = Math.min(
        this.circleRadius,
        Math.min(
          this.circleX,
          this.canvasWidth - this.circleX,
          this.circleY,
          this.canvasHeight - this.circleY
        )
      );
    } else if (this.isDragging) {
      // Move operation
      this.circleX += deltaX;
      this.circleY += deltaY;

      // Constrain to canvas bounds
      this.circleX = Math.max(
        this.circleRadius,
        Math.min(this.canvasWidth - this.circleRadius, this.circleX)
      );
      this.circleY = Math.max(
        this.circleRadius,
        Math.min(this.canvasHeight - this.circleRadius, this.circleY)
      );
    }

    this.lastMouseX = mouseX;
    this.lastMouseY = mouseY;

    // Redraw canvas and update preview
    this.drawCanvas();
    this.updatePreview();

    event.preventDefault();
  }

  onMouseUp(): void {
    this.isDragging = false;
    this.isResizing = false;
  }

  onMouseLeave(): void {
    this.isDragging = false;
    this.isResizing = false;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    console.log(
      "[ImageCropper] Save button clicked, cropped image length:",
      this.croppedImage?.length || 0
    );
    this.dialogRef.close(this.croppedImage);
  }
}
